import { type SharedData } from '@/types';
import { router } from '@inertiajs/react';
import { useEffect, useState } from 'react';

type ImpersonatingData = SharedData['impersonating'];

function getInitialImpersonating(): ImpersonatingData {
    if (typeof document === 'undefined') {
        return null;
    }

    const pageJson = document.getElementById('app')?.dataset.page;

    if (!pageJson) {
        return null;
    }

    try {
        const parsed = JSON.parse(pageJson) as { props?: SharedData };

        return parsed.props?.impersonating ?? null;
    } catch {
        return null;
    }
}

export function ImpersonationBanner() {
    const [impersonating, setImpersonating] = useState<ImpersonatingData>(() => getInitialImpersonating());
    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

    useEffect(() => {
        const handleNavigate = (event: Event) => {
            const navigateEvent = event as CustomEvent<{ page?: { props?: SharedData & { errors?: Record<string, string> } } }>;
            setImpersonating(navigateEvent.detail?.page?.props?.impersonating ?? null);
            // Message of BlockActionsDuringImpersonation when a payment or a deletion was refused.
            setBlockedMessage(navigateEvent.detail?.page?.props?.errors?.impersonation ?? null);
        };

        document.addEventListener('inertia:navigate', handleNavigate as EventListener);

        return () => {
            document.removeEventListener('inertia:navigate', handleNavigate as EventListener);
        };
    }, []);

    if (!impersonating) return null;

    // CN-191 etapa 4h: fixed red bar while the admin acts as the advertiser; payments and deletions are blocked.
    const name = impersonating.workspace_name || impersonating.user_name;

    return (
        <div
            role="status"
            className="fixed top-0 right-0 left-0 z-[70] flex flex-wrap items-center justify-between gap-2 bg-red-700 px-4 py-2 text-sm font-medium text-white shadow-md"
        >
            <span className="min-w-0">
                Você está como <strong>{name}</strong> ({impersonating.user_email}) · pagamentos e exclusões bloqueados
                {blockedMessage && <span className="block text-xs font-normal text-red-100">{blockedMessage}</span>}
            </span>
            <button
                onClick={() => router.post('/stop-impersonating')}
                className="shrink-0 rounded bg-white px-3 py-1 text-red-800 transition-colors hover:bg-red-50"
            >
                Voltar para o admin
            </button>
        </div>
    );
}
